import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import { normalizeProtocolItemInDto } from 'shared/domain/inspection/mapping/normalizeProtocolItemInDto';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { LogLevel } from 'shared/types/logger';
import { ProtocolAnswer } from 'shared/domain/protocolAnswer';
import { ComplianceCheckInDto } from 'shared/dtos/in/complianceCheck';
import { InspectionInDto } from 'shared/dtos/in/inspection';
import { ProtocolItemInDto } from 'shared/dtos/in/protocolItem';
import { ProtocolSectionInDto } from 'shared/dtos/in/protocolSection';
import {
  ComplianceCheckEntity,
  InspectionEntity,
  ProtocolItemEntity,
  ProtocolSectionEntity,
} from './entity';

function complianceCheckDtoToEntity(
  complianceCheckInDto: ComplianceCheckInDto
): ComplianceCheckEntity {
  return {
    issues: complianceCheckInDto.issues ?? [],
    result: complianceCheckInDto.result,
    comment: complianceCheckInDto.comment,
  };
}

function protocolDtoToEntity(
  protocol: ProtocolItemInDto
): ProtocolItemEntity {
  const normalized = normalizeProtocolItemInDto(protocol);
  return {
    _id: normalized._id,
    complianceCheck: complianceCheckDtoToEntity(normalized.complianceCheck!),
    modifiedAt: normalized.modifiedAt,
    modifiedBy: normalized.modifiedBy,
    templateChecklistItem: normalized.templateChecklistItem,
    answers: normalized.answers as ProtocolAnswer[] | undefined,
  };
}

function protocolSectionDtoToEntity(
  section: ProtocolSectionInDto
): ProtocolSectionEntity {
  return {
    _id: section._id,
    checklistSectionId: section.checklistSectionId,
    title: section.title,
    comment: section.comment,
    description: section.description,
    order: section.order,
    parentSectionId: section.parentSectionId,
    createdAt: section.createdAt,
    createdBy: section.createdBy,
    modifiedAt: section.modifiedAt,
    modifiedBy: section.modifiedBy,
  };
}

function resolveIsCompleted(inspection: InspectionInDto): boolean {
  if (inspection.status == null) {
    // Should be unreachable per the InspectionInDto contract (status is
    // required post-PT-6688) - if it fires, something served an old-shape
    // response (stale cache, rollout skew), and silently defaulting to
    // "not completed" would misrepresent already-completed inspections.
    swLog(
      `inspectionDtoToEntityToSave: inspection ${inspection._id} is missing 'status' - defaulting isCompleted to false.`,
      LogLevel.ERROR,
      null,
      null
    );
    return false;
  }

  return inspection.status === InspectionStatus.completed;
}

export function inspectionDtoToEntityToSave(
  inspection: InspectionInDto
): InspectionEntity {
  return {
    _id: inspection._id,
    comment: inspection.comment,
    contracts: inspection.contracts,
    controlledParty: inspection.controlledParty,
    createdAt: inspection.createdAt,
    createdBy: inspection.createdBy,
    deleted: inspection.deleted ? 1 : 0,
    inspectionDate: inspection.inspectionDate,
    isCompleted: resolveIsCompleted(inspection),
    levels: inspection.levels,
    modifiedAt: inspection.modifiedAt,
    modifiedBy: inspection.modifiedBy,
    protocol: inspection.protocol.map(protocolDtoToEntity),
    sections: (inspection.sections || []).map(protocolSectionDtoToEntity),
    sites: inspection.sites,
    template: inspection.template,
    workTypes: inspection.workTypes,
  };
}
